import { useOnline } from "@/app/online";
import { type PickedContact, pickContact } from "@/lib/contactPicker";
import { absoluteDateTime } from "@/lib/datetime";
import { formatPhoneDisplay, normalizePhone } from "@/lib/phone";
import { BottomSheet } from "@/screens/common/BottomSheet";
import { ContactRound, LockKeyhole, TriangleAlert, WifiOff } from "lucide-react";
import { type FormEvent, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { FieldMachinePicker } from "./FieldMachinePicker";
import {
  type AgendaContact,
  type FieldLeadDraft,
  asManualDraft,
  clearFieldLeadDraft,
  isEmptyFieldLeadDraft,
  saveFieldLeadDraft,
} from "./draftStore";
import { type FieldLeadProblem, fieldLeadProblem, machineDescription } from "./fieldLeadView";
import { fieldLeadHistory, subscribeToFieldLeadHistory } from "./history";
import { type FieldLeadResult, useFieldLeadLoad } from "./useFieldLeadLoad";

const MISSING_FIELDS_TEXT = "Faltan datos: los cuatro son obligatorios.";
export const MISSING_AGENDA_FIELDS_TEXT =
  "Faltan datos: el contacto, la máquina y lo que pasó en la charla son obligatorios.";
export const MISSING_NAME_TEXT = "Falta el nombre de la persona: escribilo arriba.";
export const CONTACT_WITHOUT_PHONE_TEXT =
  "Ese contacto no tiene teléfono en tu agenda. Agregáselo o elegí otro.";
export const CONTACT_BLANK_TEXT =
  "Ese contacto no tiene nombre ni teléfono en tu agenda. Completalo o elegí otro.";
export const AGENDA_FAILED_TEXT = "No pudimos abrir tu agenda. Cargalo a mano.";
export const NOT_IN_AGENDA_HINT = "Si no la tenés agendada, agendala en tu teléfono y elegila acá.";
export const DISCARD_DRAFT_HINT =
  "Vas a descartar lo que cargaste de este dato: no queda guardado en el teléfono.";
export const ALREADY_LOADED_TEXT =
  "Este dato ya se cargó mientras estabas en otra pantalla. Lo ves en «Lo que cargaste».";
const QUEUED_TEXT =
  "Sin señal: el dato queda guardado en el teléfono y se manda solo apenas vuelvan los datos. Todavía no está en el CRM.";
export const SESSION_QUEUED_TEXT =
  "Sin sesión: el dato queda guardado en el teléfono y se manda solo cuando retomes la sesión. Todavía no está en el CRM.";
const OFFLINE_TEXT = "Estás sin señal: el dato se guarda en el teléfono y sale solo cuando vuelva.";

export const UNNAMED_CONTACT_TEXT = "Contacto sin nombre";

const NAME_MAX_LENGTH = 255;
const PHONE_MAX_LENGTH = 64;
export const PICKED_PHONE_HINT = "El teléfono sale de tu agenda.";

function contactRejection(picked: PickedContact): string | null {
  if (picked.phones.length > 0) {
    return null;
  }
  return picked.name === "" ? CONTACT_BLANK_TEXT : CONTACT_WITHOUT_PHONE_TEXT;
}

function missingText(draft: FieldLeadDraft): string {
  if (draft.source === "manual") {
    return MISSING_FIELDS_TEXT;
  }
  const contact = draft.agendaContact;
  const onlyNameMissing =
    contact !== null &&
    contact.name.trim() === "" &&
    draft.machine !== null &&
    draft.comment.trim() !== "";
  return onlyNameMissing ? MISSING_NAME_TEXT : MISSING_AGENDA_FIELDS_TEXT;
}

function displayPhone(phone: string): string {
  return formatPhoneDisplay(normalizePhone(phone) ?? phone);
}

function AgendaContactField({
  nameId,
  contact,
  candidate,
  error,
  missing,
  phoneAtFault,
  choosing,
  onPick,
  onChoosePhone,
  onRename,
}: {
  nameId: string;
  contact: AgendaContact | null;
  candidate: PickedContact | null;
  error: string | null;
  missing: boolean;
  phoneAtFault: boolean;
  choosing: boolean;
  onPick: () => void;
  onChoosePhone: (phone: string) => void;
  onRename: (name: string) => void;
}) {
  const busy = choosing ? "true" : undefined;
  return (
    <div className="sheet__field">
      <span className="sheet__legend">La persona</span>
      {candidate !== null ? (
        <div className="field-lead__phones">
          <p className="t-body-strong">{candidate.name || UNNAMED_CONTACT_TEXT}</p>
          <p className="t-small">Tiene varios teléfonos. ¿A cuál le escribimos?</p>
          {candidate.phones.map((phone) => (
            <button
              key={phone}
              type="button"
              className="btn btn--block"
              disabled={choosing}
              onClick={() => onChoosePhone(phone)}
            >
              {displayPhone(phone)}
            </button>
          ))}
          <button
            type="button"
            className="btn btn--block"
            disabled={choosing}
            aria-busy={busy}
            onClick={onPick}
          >
            <ContactRound size={20} aria-hidden="true" />
            Elegir otro contacto
          </button>
        </div>
      ) : contact === null ? (
        <>
          <button
            type="button"
            className="btn btn--block"
            disabled={choosing}
            aria-busy={busy}
            aria-invalid={missing ? "true" : undefined}
            onClick={onPick}
          >
            <ContactRound size={20} aria-hidden="true" />
            Elegir de la agenda
          </button>
          <p className="t-small field-lead__hint">{NOT_IN_AGENDA_HINT}</p>
        </>
      ) : (
        <div className="field-lead__picked">
          <label className="sheet__field" htmlFor={nameId}>
            <span className="sheet__legend">Nombre de la persona</span>
            <input
              id={nameId}
              className="sheet__input"
              value={contact.name}
              maxLength={NAME_MAX_LENGTH}
              autoComplete="off"
              aria-invalid={missing && contact.name.trim() === "" ? "true" : undefined}
              onChange={(event) => onRename(event.target.value)}
            />
          </label>
          <div className="card field-lead__picked-phone">
            <span className="t-body-strong">{displayPhone(contact.phone)}</span>
            <span className="t-small">{PICKED_PHONE_HINT}</span>
          </div>
          <button
            type="button"
            className="btn btn--block"
            disabled={choosing}
            aria-busy={busy}
            aria-invalid={phoneAtFault ? "true" : undefined}
            onClick={onPick}
          >
            <ContactRound size={20} aria-hidden="true" />
            Elegir otro contacto
          </button>
        </div>
      )}
      {error === null ? null : (
        <p className="sheet__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function FieldLeadForm({
  ownerKey,
  initial,
  initialProblem = null,
  onPark,
  onDelivered,
  onDiscard,
  onLoadAnother,
}: {
  ownerKey: string;
  initial: FieldLeadDraft;
  initialProblem?: FieldLeadProblem | null;
  onPark: (draft: FieldLeadDraft) => void;
  onDelivered: (clientLoadId: string) => void;
  onDiscard: () => void;
  onLoadAnother: () => void;
}) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const online = useOnline();
  const [draft, setDraft] = useState<FieldLeadDraft>(initial);
  const [candidate, setCandidate] = useState<PickedContact | null>(null);
  const [agendaError, setAgendaError] = useState<string | null>(null);
  const [agendaFailed, setAgendaFailed] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [picking, setPicking] = useState(false);
  const [missing, setMissing] = useState(false);
  const [problem, setProblem] = useState<FieldLeadProblem | null>(initialProblem);
  const [result, setResult] = useState<FieldLeadResult | null>(null);
  const choosingRef = useRef(false);
  const firstRun = useRef(true);
  const persisted = useRef(!isEmptyFieldLeadDraft(initial));

  const load = useFieldLeadLoad(ownerKey, initial.clientLoadId, (done) => {
    persisted.current = false;
    void clearFieldLeadDraft(initial.clientLoadId);
    onDelivered(initial.clientLoadId);
    setResult(done);
  });

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (result !== null) {
      return;
    }
    if (isEmptyFieldLeadDraft(draft)) {
      if (persisted.current) {
        persisted.current = false;
        void clearFieldLeadDraft(draft.clientLoadId);
      }
      return;
    }
    persisted.current = true;
    void saveFieldLeadDraft(ownerKey, draft);
  }, [ownerKey, draft, result]);

  const history = useSyncExternalStore(
    subscribeToFieldLeadHistory,
    fieldLeadHistory,
    fieldLeadHistory,
  );
  const alreadyLoaded =
    result === null &&
    !load.isPending &&
    !load.isSuccess &&
    history.some((record) => record.clientLoadId === initial.clientLoadId);

  function update(patch: Partial<FieldLeadDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function takeAgendaContact(contact: AgendaContact) {
    update({ agendaContact: contact });
    setCandidate(null);
    if (problem?.phoneAtFault) {
      setProblem(null);
    }
  }

  function applyPicked(picked: PickedContact) {
    const [firstPhone, ...others] = picked.phones;
    const rejection = contactRejection(picked);
    setAgendaError(rejection);
    if (rejection !== null || firstPhone === undefined) {
      return;
    }
    if (others.length === 0) {
      takeAgendaContact({ name: picked.name, phone: firstPhone });
      return;
    }
    update({ agendaContact: null });
    setCandidate(picked);
  }

  function fallBackToManual() {
    setDraft((current) => asManualDraft(current));
    setCandidate(null);
    setAgendaError(null);
    setAgendaFailed(true);
  }

  async function chooseFromAgenda() {
    if (choosingRef.current) {
      return;
    }
    choosingRef.current = true;
    setChoosing(true);
    try {
      const picked = await pickContact();
      if (picked !== null) {
        applyPicked(picked);
      }
    } catch {
      fallBackToManual();
    } finally {
      choosingRef.current = false;
      setChoosing(false);
    }
  }

  function discard() {
    persisted.current = false;
    void clearFieldLeadDraft(initial.clientLoadId);
    onDiscard();
  }

  function askToCancel() {
    if (isEmptyFieldLeadDraft(draft)) {
      discard();
      return;
    }
    setConfirmingCancel(true);
  }

  function attempt(queue: boolean) {
    const usesAgenda = draft.source === "agenda";
    const contactName = (usesAgenda ? (draft.agendaContact?.name ?? "") : draft.name).trim();
    const contactPhone = usesAgenda ? (draft.agendaContact?.phone ?? "") : draft.phone.trim();
    const note = draft.comment.trim();
    const machine = draft.machine;
    if (contactName === "" || contactPhone === "" || note === "" || machine === null) {
      setMissing(true);
      return;
    }
    setMissing(false);
    setProblem(null);
    load.mutate(
      {
        submission: {
          body: {
            contact_name: contactName,
            contact_phone: contactPhone,
            inventory_id: machine.id,
            comment: note,
            client_load_id: draft.clientLoadId,
          },
          machineLabel: machine.label,
          contactSource: draft.source,
        },
        queue,
      },
      {
        onError: (error) => {
          const next = fieldLeadProblem(error, draft.source);
          setProblem(next);
          if (next.reopenMachines) {
            update({ machine: null });
          }
        },
      },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!load.isPending) {
      attempt(!online);
    }
  }

  if (alreadyLoaded) {
    return (
      <BottomSheet labelledBy={titleId} onClose={discard}>
        <header className="sheet__header">
          <h2 id={titleId} className="sheet__title">
            Dato ya cargado
          </h2>
        </header>
        <div className="sheet__form">
          <p className="sheet__text">{ALREADY_LOADED_TEXT}</p>
          <div className="field-lead__actions">
            <button type="button" className="btn btn--primary btn--block" onClick={discard}>
              Listo
            </button>
          </div>
        </div>
      </BottomSheet>
    );
  }

  if (result !== null) {
    return (
      <BottomSheet labelledBy={titleId} onClose={onDiscard}>
        <header className="sheet__header">
          <h2 id={titleId} className="sheet__title">
            {result.mode === "sent" ? "Dato cargado" : "Dato guardado"}
          </h2>
        </header>
        <div className="sheet__form">
          {result.mode === "sent" ? (
            <>
              <p className="t-body-strong">
                {result.receipt.listing_title} — {result.receipt.contact_name}
              </p>
              <p className="t-small">{absoluteDateTime(result.receipt.received_at)}</p>
            </>
          ) : (
            <p className="notice notice--offline">
              {result.waitsFor === "session" ? (
                <LockKeyhole size={16} aria-hidden="true" />
              ) : (
                <WifiOff size={16} aria-hidden="true" />
              )}
              <span>{result.waitsFor === "session" ? SESSION_QUEUED_TEXT : QUEUED_TEXT}</span>
            </p>
          )}
          <div className="field-lead__actions">
            <button type="button" className="btn btn--primary btn--block" onClick={onLoadAnother}>
              Cargar otro
            </button>
            <button type="button" className="btn btn--block" onClick={onDiscard}>
              Listo
            </button>
          </div>
        </div>
      </BottomSheet>
    );
  }

  if (picking) {
    return (
      <BottomSheet labelledBy={titleId} onClose={() => setPicking(false)}>
        <header className="sheet__header">
          <h2 id={titleId} className="sheet__title">
            La máquina que le mostraste
          </h2>
        </header>
        <FieldMachinePicker
          onPick={(option) => {
            update({ machine: option });
            setPicking(false);
          }}
          onCancel={() => setPicking(false)}
        />
      </BottomSheet>
    );
  }

  const usesAgenda = draft.source === "agenda";
  const machine = draft.machine;

  return (
    <BottomSheet
      labelledBy={titleId}
      onClose={() => {
        if (!load.isPending) {
          onPark(draft);
        }
      }}
    >
      <header className="sheet__header">
        <h2 id={titleId} className="sheet__title">
          Cargar un dato
        </h2>
      </header>
      <form className="sheet__form" onSubmit={handleSubmit} noValidate>
        <fieldset className="field-lead__fields" disabled={load.isPending}>
          {usesAgenda ? (
            <AgendaContactField
              nameId={`${ids}-name`}
              contact={draft.agendaContact}
              candidate={candidate}
              error={agendaError}
              missing={missing}
              phoneAtFault={problem?.phoneAtFault ?? false}
              choosing={choosing}
              onPick={() => void chooseFromAgenda()}
              onChoosePhone={(phone) => {
                if (candidate !== null) {
                  takeAgendaContact({ name: candidate.name, phone });
                }
              }}
              onRename={(name) => {
                const picked = draft.agendaContact;
                if (picked !== null) {
                  update({ agendaContact: { ...picked, name } });
                }
              }}
            />
          ) : (
            <>
              {agendaFailed ? (
                <p className="notice notice--info field-lead__fallback" role="alert">
                  {AGENDA_FAILED_TEXT}
                </p>
              ) : null}

              <label className="sheet__field" htmlFor={`${ids}-name`}>
                <span className="sheet__legend">Nombre de la persona</span>
                <input
                  id={`${ids}-name`}
                  className="sheet__input"
                  value={draft.name}
                  maxLength={NAME_MAX_LENGTH}
                  autoComplete="off"
                  aria-invalid={missing && draft.name.trim() === "" ? "true" : undefined}
                  onChange={(event) => update({ name: event.target.value })}
                />
              </label>

              <label className="sheet__field" htmlFor={`${ids}-phone`}>
                <span className="sheet__legend">Su teléfono</span>
                <input
                  id={`${ids}-phone`}
                  className="sheet__input"
                  inputMode="tel"
                  value={draft.phone}
                  maxLength={PHONE_MAX_LENGTH}
                  autoComplete="off"
                  placeholder="0341 15-555-1234"
                  aria-invalid={
                    (missing && draft.phone.trim() === "") || problem?.phoneAtFault
                      ? "true"
                      : undefined
                  }
                  onChange={(event) => update({ phone: event.target.value })}
                />
              </label>
            </>
          )}

          <div className="sheet__field">
            <span className="sheet__legend">La máquina</span>
            {machine === null ? (
              <button
                type="button"
                className="btn btn--block"
                aria-invalid={missing ? "true" : undefined}
                onClick={() => setPicking(true)}
              >
                Elegir la máquina
              </button>
            ) : (
              <button type="button" className="card card--tap" onClick={() => setPicking(true)}>
                <span className="t-body-strong">{machine.label}</span>
                <span className="t-small">{machineDescription(machine)}</span>
                <span className="t-small">Tocá para cambiarla</span>
              </button>
            )}
          </div>

          <label className="sheet__field" htmlFor={`${ids}-comment`}>
            <span className="sheet__legend">Qué pasó en la charla</span>
            <textarea
              id={`${ids}-comment`}
              className="sheet__textarea"
              rows={4}
              value={draft.comment}
              aria-invalid={missing && draft.comment.trim() === "" ? "true" : undefined}
              onChange={(event) => update({ comment: event.target.value })}
            />
          </label>
        </fieldset>

        {missing ? <p className="sheet__error">{missingText(draft)}</p> : null}

        <div className="field-lead__actions">
          {problem === null ? null : (
            <p className="notice notice--error">
              <TriangleAlert size={16} aria-hidden="true" />
              <span>{problem.message}</span>
            </p>
          )}

          {online ? null : (
            <p className="notice notice--offline">
              <WifiOff size={16} aria-hidden="true" />
              <span>{OFFLINE_TEXT}</span>
            </p>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--block"
            disabled={load.isPending}
            aria-busy={load.isPending ? "true" : undefined}
          >
            {load.isPending
              ? "Cargando…"
              : online
                ? "Cargar el dato"
                : "Guardar para cuando vuelva"}
          </button>

          {problem?.retriable && online ? (
            <button
              type="button"
              className="btn btn--block"
              disabled={load.isPending}
              onClick={() => attempt(true)}
            >
              Guardarlo y mandarlo cuando haya señal
            </button>
          ) : null}

          {confirmingCancel ? (
            <fieldset className="field-lead__confirm" disabled={load.isPending}>
              <p className="t-small">{DISCARD_DRAFT_HINT}</p>
              <button type="button" className="btn btn--block" onClick={discard}>
                Sí, descartarlo
              </button>
              <button
                type="button"
                className="btn btn--block"
                onClick={() => setConfirmingCancel(false)}
              >
                No, seguir cargando
              </button>
            </fieldset>
          ) : (
            <button
              type="button"
              className="btn btn--block"
              disabled={load.isPending}
              onClick={askToCancel}
            >
              Cancelar
            </button>
          )}
        </div>
      </form>
    </BottomSheet>
  );
}
